import { FC, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { MaskType } from '_metronic/helpers'
import { getErrorMessage } from '_metronic/helpers/AlertHelper'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  FetchState,
  FormFileInput,
  FormInput,
  FormSelect,
  FormTextArea,
  InfoAlert,
  ListLoading,
  useModal,
  useToast,
} from 'app/components'
import { ErroDeCarga } from '../../../dispositivos/core/ErroDeCarga'
import { FornecedorEdicao } from '../../core/_models'
import { createFornecedor, updateFornecedor, updateFornecedorLogo } from '../../core/_requests'
import { paraDtoFornecedor, valoresIniciaisFornecedor } from '../../core/formularioFornecedor'
import { opcoesTipoFornecedor, rotuloTipoFornecedor } from '../../../dispositivos/core/rotulos'
import { trataErroDoModal } from '../../../dispositivos/core/errosFormulario'
import { fornecedorSchema } from './validation/fornecedorSchema'
import { ErrosGerais } from '../../../dispositivos/core/ErrosGerais'
import { RodapeModal } from '../../../dispositivos/core/RodapeModal'

const CAMPOS = [
  'nome',
  'nomeFantasia',
  'cnpj',
  'inscricaoEstadual',
  'email',
  'telefone',
  'site',
  'contatoNome',
  'contatoTelefone',
  'contatoEmail',
  'tipos',
  'observacoes',
] as const

/** Cadastro/edição de fornecedor. Na edição, espera o fetcher `fornecedor` no `showModal`. */
export const FornecedorModal: FC = () => {
  const { fetchStates } = useModal()
  const estado = fetchStates['fornecedor'] as FetchState<FornecedorEdicao> | undefined
  if (estado?.error) return <ErroDeCarga error={estado.error} />
  if (estado && (estado.isLoading || !estado.data)) return <ListLoading />
  return <FormularioFornecedor fornecedor={estado?.data} />
}

const FormularioFornecedor: FC<{ fornecedor?: FornecedorEdicao }> = ({ fornecedor }) => {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const { t } = useT()
  // Depois do primeiro salvar, o cadastro já existe: um novo envio (para repetir a logo) é PUT.
  const idSalvo = useRef(fornecedor?.id)
  const [erroLogo, setErroLogo] = useState<string>()
  const [erroGeral, setErroGeral] = useState<string[]>([])
  const opcoesTipos = useMemo(() => opcoesTipoFornecedor(t), [t])

  const formik = useFormik({
    initialValues: valoresIniciaisFornecedor(fornecedor, (tipo) => ({
      value: tipo,
      label: rotuloTipoFornecedor(t, tipo),
    })),
    validationSchema: useMemo(() => fornecedorSchema(t), [t]),
    onSubmit: async (valores, helpers) => {
      setErroLogo(undefined)
      setErroGeral([])
      try {
        const dto = paraDtoFornecedor(valores)
        const salvo = idSalvo.current
          ? await updateFornecedor(idSalvo.current, dto)
          : await createFornecedor(dto)
        idSalvo.current = salvo.id
      } catch (error) {
        trataErroDoModal(error, helpers, CAMPOS, setErroGeral)
        return
      }

      if (valores.logo) {
        try {
          await updateFornecedorLogo(idSalvo.current, valores.logo)
        } catch (error) {
          // O cadastro já foi salvo: o modal não fecha calado, e o arquivo fica para nova tentativa.
          setErroLogo(
            t('pagina.fornecedores.modal.erro_logo', {
              mensagem: getErrorMessage(error) ?? t('pagina.fornecedores.modal.erro_desconhecido'),
            })
          )
          return
        }
      }

      showToast({
        variant: 'success',
        title: t(
          fornecedor ? 'pagina.fornecedores.modal.atualizado' : 'pagina.fornecedores.modal.criado'
        ),
      })
      closeModal()
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <ErrosGerais erros={erroGeral} />
      {erroLogo && (
        <InfoAlert variant='danger' role='alert'>
          {erroLogo}
        </InfoAlert>
      )}
      <fieldset className='mb-6'>
        <legend className='fs-6 fw-semibold text-gray-900 mb-4'>
          {t('pagina.fornecedores.secao.identificacao')}
        </legend>
        <div className='row g-4'>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='nome'
              label={t('pagina.fornecedores.campo.nome')}
              isRequired
            />
          </div>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='nomeFantasia'
              label={t('pagina.fornecedores.campo.nome_fantasia')}
            />
          </div>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='cnpj'
              label={t('pagina.fornecedores.campo.cnpj')}
              mask={MaskType.CNPJ}
              infoText={t('pagina.fornecedores.ajuda.cnpj')}
            />
          </div>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='inscricaoEstadual'
              label={t('pagina.fornecedores.campo.inscricao_estadual')}
            />
          </div>
          <div className='col-12'>
            <FormSelect
              formik={formik}
              name='tipos'
              label={t('pagina.fornecedores.campo.tipos')}
              options={opcoesTipos}
              isMulti
              hideSelectAll
              isRequired
              inputId='fornecedor-tipos'
            />
          </div>
        </div>
      </fieldset>

      <fieldset className='mb-6'>
        <legend className='fs-6 fw-semibold text-gray-900 mb-4'>
          {t('pagina.fornecedores.secao.contato')}
        </legend>
        <div className='row g-4'>
          <div className='col-md-6'>
            <FormInput formik={formik} name='email' label={t('pagina.fornecedores.campo.email')} />
          </div>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='telefone'
              label={t('pagina.fornecedores.campo.telefone')}
              mask={MaskType.Phone}
            />
          </div>
          <div className='col-12'>
            <FormInput formik={formik} name='site' label={t('pagina.fornecedores.campo.site')} />
          </div>
          <div className='col-md-4'>
            <FormInput
              formik={formik}
              name='contatoNome'
              label={t('pagina.fornecedores.campo.contato_nome')}
            />
          </div>
          <div className='col-md-4'>
            <FormInput
              formik={formik}
              name='contatoTelefone'
              label={t('pagina.fornecedores.campo.contato_telefone')}
              mask={MaskType.Phone}
            />
          </div>
          <div className='col-md-4'>
            <FormInput
              formik={formik}
              name='contatoEmail'
              label={t('pagina.fornecedores.campo.contato_email')}
            />
          </div>
        </div>
      </fieldset>

      <fieldset className='mb-6'>
        <legend className='fs-6 fw-semibold text-gray-900 mb-4'>
          {t('pagina.fornecedores.secao.logo')}
        </legend>
        {fornecedor?.logo?.url && (
          <img
            src={fornecedor.logo.url}
            alt={t('pagina.fornecedores.logo_alt', { nome: fornecedor.nome })}
            className='mb-4 rounded border'
            style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }}
          />
        )}
        <FormFileInput
          formik={formik}
          name='logo'
          label={t('pagina.fornecedores.campo.logo')}
          accept='image/png,image/jpeg,image/webp'
          infoText={t('pagina.fornecedores.ajuda.logo')}
        />
      </fieldset>

      <FormTextArea
        formik={formik}
        name='observacoes'
        label={t('pagina.fornecedores.campo.observacoes')}
        rows={3}
      />

      <RodapeModal enviando={formik.isSubmitting} rotuloEnviar={t('common.save')} />
    </form>
  )
}
